import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from "@material-ui/core"
import { Form, Formik } from "formik"
import { useContext } from "react"
import { useQueryClient } from "react-query"
import * as Yup from "yup"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import serialize from "~/store/serialize"

import Box from "~/components/Common/Box"
import { PERMISSIONS_OPTIONS } from "../../constants/permissions"
import useStyles from "./styles"

const CreateAdminDialog = () => {
  const classes = useStyles()
  const { dialogs, closeDialog } = useContext(AppContext)
  const { openSnackBar } = useContext(AppContext)
  const queryClient = useQueryClient()
  const thisDialog = dialogs?.["createAdminDialog"] || {}
  const { open = false, data } = thisDialog
  let createAdminMutation = thisDialog?.data?.createAdminFunc
  let refetchAdmins = thisDialog?.data?.refetchAdmins

  const schema = Yup.object().shape({
    name: Yup.string().min(1).required("Please enter a full name"),
    email: Yup.string().email().required("Please enter a valid email"),
    phone_number: Yup.string()
      .min(10, "Please enter a valid Phone Number")
      .max(10, "Please enter a valid Phone Number")
      .required("Please enter a valid Phone Number"),

    permission_group: Yup.string()
      .required("Please select a permission level")
      .oneOf(["read-only", "admin", "super"]),
    permissions: Yup.string(),
  })
  const handleClose = () => {
    closeDialog("createAdminDialog", false)
  }

  const onSubmit = (values, actions) => {
    createAdminMutation.mutate(values, {
      onSuccess: (res) => {
        serialize("admins", res.data).then((serializedData) => {
          queryClient.setQueryData("admins", (oldData) => {
            return [...oldData, serializedData]
          })
        })
        openSnackBar({
          message: "Admin Invited",
        })
      },
      onError: (err) => {
        if (err.response.data.error.includes("Duplicate entry")) {
          openSnackBar({
            message: "An invite has already been sent to this email",
          })
        } else {
          openSnackBar({
            message: "Error Inviting Admin",
          })
        }
      },
      onSettled: () => {
        actions.setSubmitting(false)
        handleClose()
        refetchAdmins()
      },
    })
  }

  return (
    <Dialog classes={{ paperWidthSm: classes.dialogMinHeight }} open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h2" className={classes.title}>
          New Admin
        </Typography>
        <Typography color="primary" variant="subtitle2">
          Invite admin to join
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          name: "",
          email: "",
          phone_number: "",
          permission_group: "read-only",
          permissions: "",
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogContent className={classes.dialogContent}>
                <Typography color="secondary" className={classes.title}>
                  Full Name
                </Typography>
                <TextField
                  type="text"
                  name="name"
                  id="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.name && errors.name}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  error={touched.name && errors.name}
                  variant="filled"
                  size="small"
                  fullWidth
                  FormHelperTextProps={{
                    className: classes.formHelperText,
                  }}
                />

                <Typography color="secondary" className={classes.title}>
                  Email
                </Typography>
                <TextField
                  type="text"
                  name="email"
                  id="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  error={touched.email && errors.email}
                  variant="filled"
                  size="small"
                  fullWidth
                  FormHelperTextProps={{
                    className: classes.formHelperText,
                  }}
                />

                <Typography color="secondary" className={classes.title}>
                  Phone Number (sms)
                </Typography>

                <TextField
                  type="phone"
                  name="phone_number"
                  id="phone"
                  value={values.phone_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.phone_number && errors.phone_number}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  error={touched.phone_number && errors.phone_number}
                  variant="filled"
                  size="small"
                  fullWidth
                  FormHelperTextProps={{
                    className: classes.formHelperText,
                  }}
                />
                <Typography color="secondary" className={classes.title}>
                  Permission Group
                </Typography>

                <FormControl
                  variant="outlined"
                  style={{ minWidth: 200, backgroundColor: "#F4F4F4" }}
                >
                  <Select
                    value={values.permission_group}
                    onChange={(e) => {
                      if (
                        e.target.value === "read-only" ||
                        e.target.value === "super"
                      ) {
                        setFieldValue("permissions", "")
                      }
                      setFieldValue("permission_group", e.target.value)
                    }}
                    onBlur={handleBlur}
                    name="permission_group"
                    error={touched.permission_group && errors.permission_group}
                    helperText={
                      touched.permission_group && errors.permission_group
                    }
                  >
                    <MenuItem value={"read-only"}>Read Only</MenuItem>
                    <MenuItem value={"admin"}>Admin</MenuItem>
                    <MenuItem value={"super"}>Super Admin</MenuItem>
                  </Select>
                </FormControl>
                <Box marginTop="16px">
                  <Typography color="secondary" className={classes.title}>
                    Permissions
                  </Typography>
                  {PERMISSIONS_OPTIONS?.map((permissionData, i) => (
                    <FormControlLabel
                      key={i}
                      style={{ width: "45%" }}
                      label={permissionData?.name}
                      control={
                        <Radio
                          onClick={(e) => {
                            const newPermission = e.target.value
                            if (values?.permissions?.includes(newPermission)) {
                              let newPermissions = values.permissions
                                .split(", ")
                                .filter((x) => x !== newPermission)
                              setFieldValue(
                                "permissions",
                                newPermissions.join(", "),
                              )
                            } else {
                              setFieldValue(
                                "permissions",
                                (values.permissions +=
                                  values.permissions.length > 0
                                    ? ", " + newPermission
                                    : newPermission),
                              )
                            }
                          }}
                          value={permissionData?.value}
                          checked={values.permissions.includes(
                            permissionData?.value,
                          )}
                          disabled={
                            values.permission_group === "read-only" ||
                            values.permission_group === "super"
                          }
                        />
                      }
                    />
                  ))}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} style={{ height: "40px" }}>
                  Cancel
                </Button>
                <LoaderButton
                  type="submit"
                  // onClick={onSubmit}
                  classoverrides={{
                    wrapper: classes.button,
                    button: {
                      root: classes.buttonRoot,
                    },
                  }}
                  variant="contained"
                  color="secondary"
                  working={isSubmitting}
                  size="small"
                >
                  add
                </LoaderButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default CreateAdminDialog
